import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { PropRow } from '../activity-drawer/ui/PropRow';
import { SearchableSelect } from '../../ui/searchable-select';

export interface UserOption {
  id: string;
  full_name?: string | null;
  email?: string | null;
  avatar_url?: string | null;
  profile_image_url?: string | null;
}

/** Pallino utente 16px: foto (avatar_url ha priorità su profile_image_url) o iniziali. */
export function UserDot({ user, size = 16 }: { user: UserOption; size?: number }) {
  const src = user.avatar_url || user.profile_image_url || null;
  const name = user.full_name || user.email || '?';
  const initials = name
    .split(' ')
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
  if (src) {
    return (
      <img
        src={src}
        alt=""
        style={{ width: size, height: size }}
        className="flex-shrink-0 rounded-full object-cover"
      />
    );
  }
  return (
    <span
      style={{ width: size, height: size, fontSize: size * 0.55 }}
      className="flex flex-shrink-0 items-center justify-center rounded-full bg-[rgba(233,30,140,0.12)] font-semibold text-[#E91E8C]"
    >
      {initials}
    </span>
  );
}

/** Trigger borderless per select dentro PropRow (stesso stile del drawer attività). */
export const ROW_TRIGGER_CLS =
  'h-auto py-0 px-0 border-none bg-transparent shadow-none ring-0 ring-offset-0 focus:ring-0 focus:ring-offset-0 justify-between text-[13px] [&_.text-muted-foreground]:text-[rgba(55,53,47,0.45)] dark:[&_.text-muted-foreground]:text-gray-500';

interface PropSelectRowProps {
  icon: React.ReactNode;
  label: string;
  required?: boolean;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  error?: string;
}

/** Riga di proprietà con select ricercabile inline (valore come testo, dropdown al click). */
export function PropSelectRow({ icon, label, required, value, onChange, options, placeholder, error }: PropSelectRowProps) {
  return (
    <div>
      <PropRow icon={icon} label={label} required={required} wrap>
        <SearchableSelect
          value={value || undefined}
          onValueChange={onChange}
          options={options}
          placeholder={placeholder || 'Vuoto'}
          ariaLabel={label}
          className="flex-1 min-w-0"
          triggerClassName={ROW_TRIGGER_CLS}
        />
      </PropRow>
      {error && <p className="ml-[8.5rem] text-[11px] text-red-500">{error}</p>}
    </div>
  );
}

interface PropTextRowProps {
  icon: React.ReactNode;
  label: string;
  required?: boolean;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
  error?: string;
  hint?: string;
  /** Adornment prima dell'input (es. pill prefisso telefonico). */
  lead?: React.ReactNode;
}

/** Riga di proprietà con input di testo nudo (niente box: il valore È il testo). */
export function PropTextRow({ icon, label, required, value, onChange, placeholder = 'Vuoto', type = 'text', error, hint, lead }: PropTextRowProps) {
  return (
    <div>
      <PropRow icon={icon} label={label} required={required}>
        {lead}
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          aria-label={label}
          className="w-full min-w-0 flex-1 bg-transparent text-[13px] text-[#37352F] dark:text-white placeholder-[rgba(55,53,47,0.4)] dark:placeholder-gray-500 focus:outline-none"
        />
      </PropRow>
      {error && <p className="ml-[8.5rem] text-[11px] text-red-500">{error}</p>}
      {!error && hint && <p className="ml-[8.5rem] text-[11px] text-[rgba(55,53,47,0.4)] dark:text-gray-500">{hint}</p>}
    </div>
  );
}

interface UserSelectRowProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  onChange: (value: string) => void;
  users: UserOption[];
  placeholder?: string;
  /** Sola lettura: il dropdown non si apre. */
  disabled?: boolean;
}

/**
 * Riga di proprietà per la scelta di un utente, con pallino foto/iniziali
 * sia nel valore selezionato sia nelle opzioni del dropdown (come da mockup).
 */
export function UserSelectRow({ icon, label, value, onChange, users, placeholder = 'Vuoto', disabled }: UserSelectRowProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const boxRef = useRef<HTMLDivElement>(null);
  const selected = users.find((u) => u.id === value) || null;

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const filtered = users.filter((u) =>
    (u.full_name || u.email || '').toLowerCase().includes(query.toLowerCase()),
  );

  return (
    <div ref={boxRef} className="relative">
      <PropRow icon={icon} label={label} onClick={disabled ? undefined : () => { setOpen((o) => !o); setQuery(''); }}>
        {selected ? (
          <span className="flex min-w-0 items-center gap-1.5">
            <UserDot user={selected} />
            <span className="truncate">{selected.full_name || selected.email}</span>
          </span>
        ) : (
          <span className="text-[rgba(55,53,47,0.45)] dark:text-gray-500">{placeholder}</span>
        )}
      </PropRow>
      {open && (
        <div className="absolute left-0 right-0 z-50 mt-0.5 overflow-hidden rounded-md border border-[rgba(55,53,47,0.16)] bg-white shadow-lg dark:border-white/15 dark:bg-[#252525]">
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cerca..."
            className="w-full border-b border-[rgba(55,53,47,0.09)] bg-transparent px-3 py-1.5 text-[13px] text-[#37352F] focus:outline-none dark:border-white/10 dark:text-white"
          />
          <div className="max-h-48 overflow-y-auto py-1">
            {value && (
              <button
                type="button"
                onClick={() => { onChange(''); setOpen(false); }}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px] text-[rgba(55,53,47,0.5)] hover:bg-[rgba(55,53,47,0.06)] dark:text-gray-400 dark:hover:bg-white/[0.06]"
              >
                Rimuovi assegnazione
              </button>
            )}
            {filtered.map((u) => (
              <button
                key={u.id}
                type="button"
                onClick={() => { onChange(u.id); setOpen(false); }}
                className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px] hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/[0.06] ${
                  u.id === value ? 'bg-[rgba(55,53,47,0.04)] font-medium' : ''
                } text-[#37352F] dark:text-white`}
              >
                <UserDot user={u} />
                <span className="truncate">{u.full_name || u.email}</span>
              </button>
            ))}
            {filtered.length === 0 && (
              <p className="px-3 py-2 text-[12px] text-[rgba(55,53,47,0.45)] dark:text-gray-500">Nessun risultato</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

interface MultiChipRowProps {
  icon: React.ReactNode;
  label: string;
  selected: string[];
  options: { value: string; label: string; color?: string | null }[];
  onAdd: (value: string) => void;
  onRemove: (value: string) => void;
  /** Testo libero → crea al volo (es. Tag). */
  onCreate?: (label: string) => void;
  addPlaceholder?: string;
  /** Tooltip per chip selezionato (es. data di assegnazione), per value. */
  hints?: Record<string, string>;
}

/** Riga multi-select a chip: chip rimovibili + aggiunta inline via select ricercabile. */
export function MultiChipRow({ icon, label, selected, options, onAdd, onRemove, onCreate, addPlaceholder = 'Aggiungi...', hints }: MultiChipRowProps) {
  const byValue = new Map(options.map((o) => [o.value, o]));
  const available = options.filter((o) => !selected.includes(o.value));
  return (
    <PropRow icon={icon} label={label} wrap>
      {selected.map((v) => {
        const opt = byValue.get(v);
        return (
          <span
            key={v}
            title={hints?.[v]}
            className="group/chip inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11.5px] font-medium"
            style={{
              backgroundColor: opt?.color ? `${opt.color}20` : 'rgba(55,53,47,0.08)',
              color: opt?.color || undefined,
            }}
          >
            <span className="text-[#37352F] dark:text-white">{opt?.label ?? v}</span>
            <button
              type="button"
              aria-label={`Rimuovi ${opt?.label ?? v}`}
              onClick={(e) => { e.stopPropagation(); onRemove(v); }}
              className="opacity-40 hover:opacity-100"
            >
              <X className="h-3 w-3" strokeWidth={1.5} />
            </button>
          </span>
        );
      })}
      <SearchableSelect
        value={undefined}
        onValueChange={(v) => {
          if (byValue.has(v)) onAdd(v);
          else if (onCreate && v.trim()) onCreate(v.trim());
        }}
        options={available.map((o) => ({ value: o.value, label: o.label }))}
        placeholder={addPlaceholder}
        allowCustomValue={!!onCreate}
        ariaLabel={`Aggiungi ${label}`}
        className="min-w-[110px] flex-1"
        triggerClassName={ROW_TRIGGER_CLS}
      />
    </PropRow>
  );
}

interface PropToggleRowProps {
  icon: React.ReactNode;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  onLabel?: string;
  offLabel?: string;
}

/** Riga di proprietà booleana: il valore è testo cliccabile (Consenso attivo / Nessun consenso). */
export function PropToggleRow({ icon, label, checked, onChange, onLabel = 'Consenso attivo', offLabel = 'Nessun consenso' }: PropToggleRowProps) {
  return (
    <PropRow icon={icon} label={label} onClick={() => onChange(!checked)}>
      <span className={checked ? 'text-[13px] text-[#37352F] dark:text-white' : 'text-[13px] text-[rgba(55,53,47,0.45)] dark:text-gray-500'}>
        {checked ? onLabel : offLabel}
      </span>
    </PropRow>
  );
}
